import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router'

import type { Icon } from '@/shared/registry'
import { useSidebarCollapsed } from '@/shared/layout/use-sidebar-collapsed'
import { usePopoverA11y } from '@/shared/search/use-popover-a11y'
import { cn } from '@/shared/ui'
import { focusRing, insetFocusRing } from '@/shared/ui/cn'
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from '@/shared/ui/icons'
import { useMediaQuery } from '@/shared/ui/use-media-query'

/** One entry of a details page's side menu: a related page (`path`) or an action (`onSelect`). */
export type DetailsMenuItem = {
  readonly id: string
  readonly label: string
  readonly icon: Icon
  /** `error` for a destructive one, such as Delete. */
  readonly tone?: 'error'
} & ({ readonly path: string } | { readonly onSelect: () => void })

/** A titled group of side-menu entries, such as "Training". */
export interface DetailsMenuGroup {
  readonly id: string
  readonly title: string
  readonly items: readonly DetailsMenuItem[]
}

/**
 * A details page's side menu, from the mockups' `details/DetailsSidebar.jsx`: grouped related
 * pages and record actions, in place of legacy's wrapping toolbar. From 1200px wide it is a side
 * column that collapses to an icon rail, remembered with the other side menus; narrower, it is one
 * "Actions & links" button that opens the same menu over the page.
 */
export function DetailsSidebar(props: { readonly groups: readonly DetailsMenuGroup[] }) {
  const wide = useMediaQuery('(min-width: 1200px)')
  return wide ? <SideColumn groups={props.groups} /> : <MenuButton groups={props.groups} />
}

function SideColumn(props: { readonly groups: readonly DetailsMenuGroup[] }) {
  const intl = useIntl()
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const toggleRef = useRef<HTMLButtonElement>(null)
  // The toggle moves when pressed, so focus follows it.
  const focusToggle = useRef(false)
  const toggle = useCallback(() => {
    focusToggle.current = true
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])
  useLayoutEffect(() => {
    if (focusToggle.current) {
      focusToggle.current = false
      toggleRef.current?.focus()
    }
  })

  const toggleLabel = intl.formatMessage({ id: collapsed ? 'details.menu.expand' : 'details.menu.collapse' })
  const toggleButton = (
    <button
      ref={toggleRef}
      type="button"
      onClick={toggle}
      aria-label={toggleLabel}
      className={cn(
        'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-secondary transition hover:bg-background-contrast-sm hover:text-primary-main',
        focusRing,
      )}
    >
      {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
    </button>
  )

  return (
    <aside className={cn('shrink-0 transition-[width] duration-200', collapsed ? 'w-14' : 'w-64')}>
      <nav
        aria-label={intl.formatMessage({ id: 'details.menu.label' })}
        className={cn(
          'sticky top-[calc(var(--shell-header-height,0px)+1.5rem)] flex flex-col',
          collapsed ? 'items-center gap-2' : 'gap-6',
        )}
      >
        {collapsed && toggleButton}
        {props.groups.map((group, index) => (
          <MenuGroup key={group.id} group={group} collapsed={collapsed}>
            {index === 0 && !collapsed && toggleButton}
          </MenuGroup>
        ))}
      </nav>
    </aside>
  )
}

// What takes a press itself, so a press on it outside the open menu leaves focus there. Not a
// container with a negative tabindex, such as the app shell's <main>.
const interactive =
  'a[href], button, input, select, textarea, label, summary, [tabindex]:not([tabindex^="-"]), [contenteditable="true"]'

function MenuButton(props: { readonly groups: readonly DetailsMenuGroup[] }) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const panelId = useId()
  const rootRef = useRef<HTMLElement>(null)
  const close = useCallback(() => {
    setOpen(false)
    buttonRef.current?.focus({ preventScroll: true })
  }, [])
  // Focus is placed here, not by the hook, so a close caused by focus moving away leaves it there.
  usePopoverA11y(open, panelRef, close, { trap: false, restoreFocus: false })
  const toggle = useCallback(() => setOpen((value) => !value), [])
  const onButtonKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (open && event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    },
    [open, close],
  )
  useEffect(() => {
    const root = rootRef.current
    if (!open || root === null) {
      return undefined
    }
    // A press outside closes it: a control pressed keeps focus, anywhere else focus returns to the button.
    function onMouseDown(event: MouseEvent) {
      const { target } = event
      if (!(target instanceof Element) || root?.contains(target) === true) {
        return
      }
      if (target.closest(interactive) === null) {
        event.preventDefault()
        close()
      } else {
        setOpen(false)
      }
    }
    // Tabbing out of the button or the panel closes it, leaving focus where it went.
    function onFocusOut(event: FocusEvent) {
      if (event.relatedTarget instanceof Node && root?.contains(event.relatedTarget) === false) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown, true)
    root.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true)
      root.removeEventListener('focusout', onFocusOut)
    }
  }, [open, close])

  return (
    <aside ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        onKeyDown={onButtonKeyDown}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-line bg-background px-3 text-sm font-medium text-text-primary transition hover:border-primary-main hover:text-primary-main sm:w-auto sm:min-w-[14rem]',
          focusRing,
        )}
      >
        {intl.formatMessage({ id: 'details.menu.button' })}
        <ChevronDown size={18} className={cn('transition', open && 'rotate-180')} />
      </button>
      {open && (
        <nav
          ref={panelRef}
          id={panelId}
          aria-label={intl.formatMessage({ id: 'details.menu.label' })}
          className="absolute top-full left-0 z-30 mt-2 flex max-h-[70vh] w-full max-w-sm flex-col gap-6 overflow-y-auto rounded-lg border border-line bg-background p-3 shadow-card-hover"
        >
          {props.groups.map((group) => (
            <MenuGroup key={group.id} group={group} collapsed={false} onChoose={close} />
          ))}
        </nav>
      )}
    </aside>
  )
}

function MenuGroup(props: {
  readonly group: DetailsMenuGroup
  readonly collapsed: boolean
  readonly onChoose?: () => void
  readonly children?: ReactNode
}) {
  const titleId = useId()
  const { group, collapsed, onChoose } = props
  return (
    <div>
      <div className={cn('mb-2 flex items-center justify-between gap-2 px-2', collapsed && 'sr-only')}>
        <p id={titleId} className="text-xs font-semibold uppercase tracking-wider text-text-light">
          {group.title}
        </p>
        {props.children}
      </div>
      <ul aria-labelledby={titleId} className={cn('flex flex-col gap-0.5', collapsed && 'items-center')}>
        {group.items.map((item) => (
          <li key={item.id}>
            <MenuEntry item={item} collapsed={collapsed} {...(onChoose === undefined ? {} : { onChoose })} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function MenuEntry(props: {
  readonly item: DetailsMenuItem
  readonly collapsed: boolean
  /** Closes the narrow menu once an entry is chosen. */
  readonly onChoose?: () => void
}) {
  const { item, collapsed, onChoose } = props
  const { icon: ItemIcon } = item
  const className = cn(
    'flex cursor-pointer items-center gap-2.5 rounded-lg border border-transparent py-2 text-left text-sm transition duration-150',
    insetFocusRing,
    collapsed ? 'h-10 w-10 justify-center px-0' : 'w-full px-2.5',
    item.tone === 'error'
      ? 'text-error-main hover:bg-error-soft'
      : 'text-text-primary hover:translate-x-0.5 hover:bg-background-contrast-sm',
  )
  const content = (
    <>
      <ItemIcon size={collapsed ? 20 : 18} className="shrink-0" />
      <span className={cn(collapsed && 'sr-only')}>{item.label}</span>
    </>
  )
  // Collapsed, only the icon shows, so a pointer gets the label as a tooltip.
  const title = collapsed ? item.label : undefined
  const buttonRef = useRef<HTMLButtonElement>(null)
  // Not every browser focuses a clicked button, and a dialog the action opens gives focus back to what held it.
  const select = useCallback(() => {
    buttonRef.current?.focus()
    onChoose?.()
    if ('onSelect' in item) {
      item.onSelect()
    }
  }, [item, onChoose])

  return 'path' in item ? (
    <Link to={item.path} title={title} onClick={onChoose} className={className}>
      {content}
    </Link>
  ) : (
    <button ref={buttonRef} type="button" onClick={select} title={title} className={className}>
      {content}
    </button>
  )
}
